import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  InitializeCutPanelInspectionDB,
  CutPanelInspectionDBActionTypes,
  SetCutPanelInspectionDB,
  CutPanelInspectionNoAction,
  UpdateRecentRecutsList,
  FetchInspectionCounts,
  FetchInspectionCountsSuccess,
} from './cut-panel-inspection.actions';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import produce from 'immer';
import { InitStoreFacade } from '../init-db/init-db.facade';
import { getCutPanelInspectionInitialState } from './cut-panel-inspection.reducer';
import { CutPanelInspectionDBFacade } from './cut-panel-inspection.facade';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { AppNotificationUtilClass, CutPanelInspectionDataProvider } from '@monorepo/services';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class CutPanelInspectionDBEffects {
  constructor(
    private action$: Actions,
    private initStoreFacade: InitStoreFacade,
    private persistentStorageService: PersistentStorageService,
    private cpiDBFacade: CutPanelInspectionDBFacade,
    private apiHelper: AppNotificationUtilClass,
    private appMasterDBFacade: AppMasterDBFacade,
    private cpiDataProvider: CutPanelInspectionDataProvider,
    private translate: TranslateService,
  ) {}

  InitializeCutPanelInspectionDB$ = createEffect(() =>
    this.action$.pipe(
      ofType<InitializeCutPanelInspectionDB>(CutPanelInspectionDBActionTypes.InitializeCutPanelInspectionDB),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheRestTimestamp) => [action, nextCacheRestTimestamp] as const),
        );
      }),
      switchMap(([action, nextCacheRestTimestamp]) =>
        from(this.persistentStorageService.get('[APP][CUT_PANEL_INSPECTION_DB]')).pipe(
          take(1),
          switchMap((cpiDB) => {
            const cpiDBInitialState = getCutPanelInspectionInitialState(nextCacheRestTimestamp);
            if (cpiDB) {
              if (cpiDB.nextCacheResetTimestamp === nextCacheRestTimestamp)
                return of(new SetCutPanelInspectionDB(cpiDB));
              else {
                return from(
                  this.persistentStorageService.set('[APP][CUT_PANEL_INSPECTION_DB]', cpiDBInitialState),
                ).pipe(
                  take(1),
                  map(() => {
                    return new SetCutPanelInspectionDB(cpiDBInitialState);
                  }),
                );
              }
            } else {
              return from(this.persistentStorageService.set('[APP][CUT_PANEL_INSPECTION_DB]', cpiDBInitialState)).pipe(
                take(1),
                map(() => {
                  return new SetCutPanelInspectionDB(cpiDBInitialState);
                }),
              );
            }
          }),
        ),
      ),
    ),
  );

  SetCutPanelInspectionDB$ = createEffect(() =>
    this.action$.pipe(
      ofType<SetCutPanelInspectionDB>(CutPanelInspectionDBActionTypes.SetCutPanelInspectionDB),
      map(() => {
        this.cpiDBFacade.cutPanelInspectionDBInitCompleted();
        return new CutPanelInspectionNoAction();
      }),
    ),
  );

  UpdateRecentRecutsList$ = createEffect(() =>
    this.action$.pipe(
      ofType<UpdateRecentRecutsList>(CutPanelInspectionDBActionTypes.UpdateRecentRecutsList),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][CUT_PANEL_INSPECTION_DB]')).pipe(
          take(1),
          switchMap((cpiDB) => {
            const cpiDBUpdated = produce(cpiDB, (draft) => {
              draft.recentRecuts.default = action.list;
            });
            return from(this.persistentStorageService.set('[APP][CUT_PANEL_INSPECTION_DB]', cpiDBUpdated)).pipe(
              take(1),
              map(() => {
                return new CutPanelInspectionNoAction();
              }),
            );
          }),
        ),
      ),
    ),
  );

  FetchInspectionCounts$ = createEffect(() =>
    this.action$.pipe(
      ofType<FetchInspectionCounts>(CutPanelInspectionDBActionTypes.FetchInspectionCounts),
      switchMap((action) =>
        from(this.apiHelper.presentLoading(
          this.translate.instant('cpi.db.loading'),
        )).pipe(
          take(1),
          map((loader) => [loader, action.filters] as const),
        ),
      ),
      switchMap(([loader, filters]) =>
        from(this.appMasterDBFacade.getAppDetailsCPI()).pipe(
          take(1),
          map((appDetails) => {
            return [loader, filters, appDetails] as const;
          }),
        ),
      ),
      switchMap(([loader, filters, appDetails]) =>
        this.cpiDataProvider.fetchCutPanelInspectionInfo({ appDetails, filters }).pipe(
          take(1),
          map((res) => new FetchInspectionCountsSuccess(res.cutPanelInspectionInfoCount)),
          catchError((error) =>
            from(this.apiHelper.showPopup(
              this.translate.instant('cpi.db.error'), 
              this.translate.instant('cpi.db.something_went_wrong_cannot_get_inspection_counts')
            )).pipe(
              take(1),
              map(() => {
                console.log('[LANDING_PAGE_COUNTS_ERROR]', error);
                return new CutPanelInspectionNoAction();
              }),
            ),
          ),
          finalize(() => this.apiHelper.dismissLoadingV2(loader)),
        ),
      ),
    ),
  );
}
